(() => {
  'use strict';
  function bind(header) {
    if (header.dataset.pixelNavBound) return;
    header.dataset.pixelNavBound = 'true';
    const roots = [...header.querySelectorAll('[data-pixel-nav-root]')];
    const desktopRoots = [...header.querySelectorAll('.pixel-nav-desktop > [data-pixel-nav-root]')];
    const subgroups = [...header.querySelectorAll('[data-pixel-nav-sub]')];
    const hoverMedia = window.matchMedia('(min-width: 901px) and (hover: hover) and (pointer: fine)');
    // An intentional pause avoids flyouts opening while crossing the navigation.
    const openDelay = 350, leaveDelay = 250;
    let openTimer, closeTimer, pendingRoot = null, hoveredRoot = null, openedByHover = null;
    let lastPointerType = '';
    const cancelOpen = () => { clearTimeout(openTimer); pendingRoot = null; };
    const cancelClose = () => clearTimeout(closeTimer);
    const cancelTimers = () => { cancelOpen(); cancelClose(); };
    const keyboardInside = detail => detail.contains(document.activeElement)
      && document.activeElement.matches(':focus-visible');
    const close = (detail, returnFocus = false) => {
      if (pendingRoot === detail) cancelOpen();
      if (openedByHover === detail) openedByHover = null;
      detail.open = false;
      if (returnFocus) detail.querySelector(':scope > summary')?.focus();
    };
    const scheduleClose = () => {
      cancelClose();
      closeTimer = setTimeout(() => {
        if (!hoverMedia.matches || pendingRoot) return;
        desktopRoots.filter(detail => detail.open).forEach(detail => {
          if (hoveredRoot !== detail && !keyboardInside(detail)) close(detail);
        });
      }, leaveDelay);
    };
    const mouseHover = event => hoverMedia.matches && event.pointerType === 'mouse';
    for (const detail of desktopRoots) {
      const summary = detail.querySelector(':scope > summary');
      const enter = event => {
        if (!mouseHover(event)) return;
        hoveredRoot = detail;
        cancelClose();
        if (pendingRoot && pendingRoot !== detail) cancelOpen();
      };
      detail.addEventListener('pointerenter', enter);
      detail.querySelector('.pixel-nav-panel')?.addEventListener('pointerenter', enter);
      summary.addEventListener('pointerenter', event => {
        if (!mouseHover(event)) return;
        enter(event);
        cancelOpen();
        if (detail.open) return;
        pendingRoot = detail;
        openTimer = setTimeout(() => {
          if (pendingRoot !== detail || !hoverMedia.matches || !summary.matches(':hover')) return;
          pendingRoot = null;
          // Hover must not hide links someone is currently using with a keyboard.
          if (desktopRoots.some(other => other !== detail && other.open && keyboardInside(other))) return;
          roots.forEach(other => { if (other !== detail) close(other); });
          detail.open = true;
          openedByHover = detail;
        }, openDelay);
      });
      detail.addEventListener('pointerleave', event => {
        if (!mouseHover(event)) return;
        if (hoveredRoot === detail) hoveredRoot = null;
        if (pendingRoot === detail) cancelOpen();
        scheduleClose();
      });
    }
    for (const detail of roots) {
      detail.addEventListener('toggle', () => {
        if (detail.open) roots.forEach(other => { if (other !== detail) close(other); });
        else if (openedByHover === detail) openedByHover = null;
      });
    }
    for (const detail of subgroups) {
      detail.addEventListener('toggle', () => {
        if (detail.open) subgroups.forEach(other => { if (other !== detail) close(other); });
      });
    }
    header.addEventListener('pointerdown', event => {
      lastPointerType = event.pointerType;
      cancelTimers();
    });
    header.addEventListener('keydown', event => {
      cancelTimers();
      if (event.key !== 'Escape') return;
      const local = event.target.closest('[data-pixel-nav-sub][open]');
      const root = event.target.closest('[data-pixel-nav-root][open]');
      const target = local || root || roots.find(detail => detail.open);
      if (target) { event.preventDefault(); close(target, true); }
    });
    // A hover-only flyout does not move focus into the header. Escape must still dismiss it.
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || header.contains(event.target)) return;
      cancelTimers();
      roots.filter(detail => detail.open).forEach(detail => close(detail));
    });
    document.addEventListener('pointerdown', event => {
      if (header.contains(event.target)) return;
      cancelTimers();
      roots.filter(detail => detail.open).forEach(detail => close(detail, detail.contains(document.activeElement)));
    });
    header.addEventListener('focusout', event => {
      if (!event.relatedTarget) return;
      // Native subgroup activation owns mobile layout; do not collapse it at pointer focus.
      for (const detail of roots) {
        if (detail.open && !detail.contains(event.relatedTarget)) close(detail);
      }
    });
    header.addEventListener('click', event => {
      cancelTimers();
      const summary = event.target.closest('.pixel-nav-desktop > details > summary');
      if (summary && event.detail > 0 && lastPointerType === 'mouse'
        && openedByHover === summary.parentElement && summary.parentElement.open) {
        // The first click after hover confirms the open menu instead of collapsing it.
        event.preventDefault();
        openedByHover = null;
        return;
      }
      if (!event.target.closest('a')) return;
      roots.filter(detail => detail.open).forEach(detail => close(detail));
    });
    const markCurrent = () => {
      const current = new URL(location.href);
      for (const link of header.querySelectorAll('a')) {
        const target = new URL(link.href);
        const sameQuery = [...target.searchParams].every(([key, value]) => current.searchParams.get(key) === value);
        if (target.origin === current.origin && target.pathname === current.pathname && target.hash === current.hash && sameQuery) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      }
    };
    markCurrent();
    window.addEventListener('hashchange', markCurrent);
    const resetInputMode = () => {
      cancelTimers(); hoveredRoot = null; openedByHover = null;
      for (const detail of [...roots, ...subgroups]) close(detail);
    };
    window.matchMedia('(max-width: 900px)').addEventListener('change', resetInputMode);
    hoverMedia.addEventListener('change', resetInputMode);
    window.addEventListener('blur', cancelTimers);
    document.addEventListener('shopify:section:unload', event => {
      if (event.target.contains(header)) cancelTimers();
    });
  }
  function initialize() { document.querySelectorAll('[data-pixel-navigation]').forEach(bind); }
  initialize();
  document.addEventListener('shopify:section:load', initialize);
})();
